import { toast } from "sonner";
import { X } from "lucide-react";
import { formatCurrency, formatUSD, getUsdRate } from "@vaulzo/core/constants";
import { getStockQuote } from "@vaulzo/core/api/market";
import StockSelect from "./StockSelect";

const underline =
  "border-0 border-b-[1.5px] border-vaulzo-input-line bg-transparent py-2 text-vaulzo-ink outline-none transition-colors placeholder:text-vaulzo-subtle focus:border-vaulzo-accent";

/**
 * Row-based editor for a US-stocks portfolio: ticker search + units +
 * optional buy price per row, plus the uninvested USD cash and the total
 * INR spent converting to USD (which powers the true INR-basis gain).
 * The server does the authoritative pricing on save.
 */
export default function StockHoldingsEditor({
  holdings,
  setHoldings,
  cashUsd,
  setCashUsd,
  investedInr,
  setInvestedInr,
  exchangeRates,
}) {
  const patchRow = (i, patch) =>
    setHoldings((rows) => rows.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  const removeRow = (i) => setHoldings((rows) => rows.filter((_, idx) => idx !== i));
  const addRow = () =>
    setHoldings((rows) => [...rows, { symbol: "", name: "", units: "", buyPrice: "", lastPrice: null }]);

  // Selecting a ticker pulls its quote — which also rejects non-USD lines.
  const selectStock = async (i, stock) => {
    patchRow(i, { ...stock, lastPrice: null });
    try {
      const res = await getStockQuote(stock.symbol);
      patchRow(i, { lastPrice: parseFloat(res.price_usd) || null, name: res.name || stock.name });
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not fetch a USD quote for ${stock.symbol}`);
      patchRow(i, { symbol: "", name: "", lastPrice: null });
    }
  };

  // Running totals across rows with a known price.
  let holdingsUSD = 0;
  let costUSD = 0;
  let costNowUSD = 0;
  holdings.forEach((h) => {
    const units = parseFloat(h.units);
    if (!(units > 0) || !(h.lastPrice > 0)) return;
    holdingsUSD += units * h.lastPrice;
    const buy = parseFloat(h.buyPrice);
    if (buy > 0) {
      costUSD += units * buy;
      costNowUSD += units * h.lastPrice;
    }
  });
  const cash = parseFloat(cashUsd) > 0 ? parseFloat(cashUsd) : 0;
  const totalUSD = holdingsUSD + cash;
  const totalINR = totalUSD * getUsdRate(exchangeRates);
  const usdGain = costUSD > 0 ? ((costNowUSD - costUSD) / costUSD) * 100 : null;
  const invested = parseFloat(investedInr);
  const inrGain = invested > 0 && totalUSD > 0 ? ((totalINR - invested) / invested) * 100 : null;

  const gainSpan = (g, label) => (
    <span className={`font-semibold ${g >= 0 ? "text-vaulzo-accent" : "text-vaulzo-danger"}`}>
      {" "}
      · {g >= 0 ? "↑" : "↓"} {Math.abs(g).toFixed(2)}% <span className="font-normal text-vaulzo-fainter">{label}</span>
    </span>
  );

  return (
    <div className="block">
      <div className="mb-3 flex items-baseline justify-between">
        <span className="text-[13px] font-semibold text-vaulzo-muted">Holdings</span>
        <span className="text-[12px] text-vaulzo-fainter">Buy price is optional — shows your $ gain</span>
      </div>

      {holdings.map((h, i) => {
        const units = parseFloat(h.units);
        const rowUSD = units > 0 && h.lastPrice > 0 ? units * h.lastPrice : null;
        const buy = parseFloat(h.buyPrice);
        const rowGain = rowUSD && buy > 0 ? ((h.lastPrice - buy) / buy) * 100 : null;

        return (
          <div key={i} className="border-b border-vaulzo-row py-3 first:pt-1 last:border-b-0">
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <StockSelect
                  value={h.symbol ? `${h.symbol} — ${h.name || ""}` : ""}
                  onSelect={(stock) => selectStock(i, stock)}
                />
              </div>
              <button
                type="button"
                onClick={() => removeRow(i)}
                disabled={holdings.length === 1}
                aria-label="Remove holding"
                className="flex w-5 flex-none justify-center text-vaulzo-bar transition-colors hover:text-vaulzo-danger disabled:opacity-30"
              >
                <X className="h-4 w-4" strokeWidth={1.7} />
              </button>
            </div>

            <div className="mt-2 flex gap-4">
              <label className="flex-1">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
                  Units
                </span>
                <input
                  type="number"
                  step="any"
                  min="0"
                  inputMode="decimal"
                  required
                  value={h.units}
                  onChange={(e) => patchRow(i, { units: e.target.value })}
                  placeholder="209.60"
                  className={`num w-full text-[15px] font-semibold ${underline}`}
                />
              </label>
              <label className="w-[130px]">
                <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
                  Buy price ($)
                </span>
                <input
                  type="number"
                  step="any"
                  min="0"
                  inputMode="decimal"
                  value={h.buyPrice}
                  onChange={(e) => patchRow(i, { buyPrice: e.target.value })}
                  placeholder="19.08"
                  className={`num w-full text-[15px] ${underline}`}
                />
              </label>
            </div>

            {rowUSD && (
              <div className="num pt-2 text-[12px] text-vaulzo-fainter">
                ≈ {formatUSD(rowUSD, { decimals: 2, suffix: "" })} at{" "}
                {formatUSD(h.lastPrice, { decimals: 2, suffix: "" })}
                {rowGain != null && (
                  <span className={rowGain >= 0 ? "text-vaulzo-accent" : "text-vaulzo-danger"}>
                    {" "}
                    · {rowGain >= 0 ? "↑" : "↓"} {Math.abs(rowGain).toFixed(2)}%
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}

      <button
        type="button"
        onClick={addRow}
        className="mt-3 text-[13px] font-semibold text-vaulzo-accent transition-colors hover:text-vaulzo-accent-hover"
      >
        + Add holding
      </button>

      {/* Cash + INR invested */}
      <div className="mt-5 flex gap-4">
        <label className="flex-1">
          <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
            Uninvested cash ($)
          </span>
          <input
            type="number"
            step="any"
            min="0"
            inputMode="decimal"
            value={cashUsd}
            onChange={(e) => setCashUsd(e.target.value)}
            placeholder="12.35"
            className={`num w-full text-[15px] ${underline}`}
          />
        </label>
        <label className="flex-1">
          <span className="mb-1 block text-[11px] uppercase tracking-[0.06em] text-vaulzo-fainter">
            Total ₹ converted to USD
          </span>
          <input
            type="number"
            step="any"
            min="0"
            inputMode="decimal"
            value={investedInr}
            onChange={(e) => setInvestedInr(e.target.value)}
            placeholder="478963.24"
            className={`num w-full text-[15px] ${underline}`}
          />
        </label>
      </div>
      <div className="mt-1 text-[12px] text-vaulzo-fainter">
        The ₹ figure powers your true INR gain — market and FX moves together.
      </div>

      {/* Portfolio total with both gain views */}
      {totalUSD > 0 && (
        <div className="num mt-4 rounded-xl bg-vaulzo-row/60 px-4 py-3 text-[13px] text-vaulzo-muted">
          Total{" "}
          <span className="font-semibold text-vaulzo-ink">
            {formatUSD(totalUSD, { decimals: 2, suffix: "" })}
          </span>{" "}
          ≈ {formatCurrency(totalINR)}
          {usdGain != null && gainSpan(usdGain, "($)")}
          {inrGain != null && gainSpan(inrGain, "(₹ vs invested)")}
        </div>
      )}
    </div>
  );
}
